import Vue from 'vue';

const $axios = Vue.axios;

function getNova() {
  return Vue.prototype.$nova;
}

export default {
  namespaced: true,

  state: {
    metadata: {},
    tb: [],
    checklist: [],
    reflist: [],
    summary: {},
    pbcdata: {},
    formdata: {},
    userlist: {},
    userNameList: [],
    lastRunData: [],
    erpRunningStatus: false,
    locationtaskList: [],
    loading: false,
  },

  getters: {
    metadata: state => state.metadata,
    tb: state => state.tb,
    checklist: state => state.checklist,
    reflist: state => state.reflist,
    summary: state => state.summary,
    pbcdata: state => state.pbcdata,
    formdata: state => state.formdata,
    userlist: state => state.userlist,
    userNameList: state => state.userNameList,
    lastRunData: state => state.lastRunData,
    erpRunningStatus: state => state.erpRunningStatus,
    locationtaskList: state => state.locationtaskList,
    loading: state => state.loading,
  },

  mutations: {
    SET_METADATA(state, payload) { state.metadata = payload; },
    SET_TB(state, payload) { state.tb = payload; },
    SET_CHECKLIST(state, payload) { state.checklist = payload; },
    SET_REFLIST(state, payload) { state.reflist = payload; },
    SET_SUMMARY(state, payload) { state.summary = payload; },
    SET_PBCDATA(state, payload) { state.pbcdata = payload; },
    SET_FORMDATA(state, payload) { state.formdata = payload; },
    SET_USERLIST(state, payload) { state.userlist = payload; },
    SET_USERNAME_LIST(state, payload) { state.userNameList = payload; },
    SET_LAST_RUN_DATA(state, payload) { state.lastRunData = payload; },
    SET_ERP_RUNNING_STATUS(state, payload) { state.erpRunningStatus = payload; },
    SET_LOCATION_TASK_LIST(state, payload) { state.locationtaskList = payload; },
    SET_LOADING(state, payload) { state.loading = payload; },
  },

  actions: {
    async fetchDetails({ commit }, { id }) {
      commit('SET_LOADING', true);
      try {
        // Parallel: fetch metadata + TB rows + form data
        const [detailsRes, tbRes, formRes] = await Promise.all([
          $axios.post(`/v3/financialclose/analysis/getdetails/${id}`, { page: 'tb' }),
          $axios.post(`/v3/financialclose/analysis/gettrialbalance/${id}`, {}),
          $axios.post('/v3/financialclose/analysis/getformdata'),
        ]);

        // Process details (metadata)
        if (detailsRes.data.status === 'success') {
          const detailsData = detailsRes.data.data || {};
          commit('SET_METADATA', detailsData.metadata || {});
          commit('SET_SUMMARY', detailsData.summary || {});
        } else {
          throw new Error(detailsRes.data.message || 'Failed to fetch details');
        }

        // Process trial balance rows
        if (tbRes.data.status === 'success') {
          const tbData = tbRes.data.data || {};
          const tbRecords = tbData.records || [];
          commit('SET_TB', tbRecords);
        } else {
          throw new Error(tbRes.data.message || 'Failed to fetch trial balance');
        }

        // Process form data (users)
        if (formRes.data.status === 'success') {
          const formData = formRes.data.data || {};
          commit('SET_FORMDATA', formData);
          commit('SET_USERNAME_LIST', formData.users || []);
          const $nova = getNova();
          const userlist = {};
          const colors = $nova ? $nova.getColors((formData.users || []).length, 0.2) : [];
          for (let i = 0; i < (formData.users || []).length; i++) {
            const el = (formData.users || [])[i];
            userlist[el._id] = { ...el, color: colors[i] };
          }
          commit('SET_USERLIST', userlist);
        } else {
          throw new Error(formRes.data.message || 'Failed to fetch form data');
        }

        return true;
      } catch (error) {
        console.error('[trialBalance/fetchDetails]:', error);
        commit('sbError', error?.response?.data?.message || error?.message || 'Failed to fetch trial balance data', { root: true });
        return false;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Fetch a single enriched task on demand (for task detail drawer).
     * Calls /v3/financialclose/analysis/gettask/:id/task/:taskId
     */
    async fetchTask(_, { projectId, taskId }) {
      try {
        const response = await $axios.post(`/v3/financialclose/analysis/gettask/${projectId}/task/${taskId}`);
        const dt = response.data;
        if (dt.status !== 'success' || !dt.data) throw new Error('Failed to fetch task');
        return dt.data;
      } catch (err) {
        console.error('[trialBalance/fetchTask]:', err);
        throw err;
      }
    },

    async fetchLastUpdatedAt({ commit, rootState }, { id, startDate, endDate }) {
      try {
        if (!rootState.erpEnabled?.tbErpEnabled) return;
        const body = { project: id, startDate, endDate, type: 'trialbalance' };
        const { data } = await $axios.post('/v2/erp/get-status', body);
        if (data?.status === 'success') {
          commit('SET_LAST_RUN_DATA', data.data);
        }
      } catch (e) {
        console.error('[trialBalance/fetchLastUpdatedAt]: Failed to fetch ERP TB status', e);
      }
    },

    async stopErpPipeline({ commit, rootState }, { id, startDate, endDate }) {
      try {
        if (!rootState.erpEnabled?.tbErpEnabled) return;
        const body = { project: id, startDate, endDate, type: 'stop', processgroup: 'Skinlaundry-TB-pipeline' };
        const { data } = await $axios.post('/v2/erp/check-status', body);
        if (data?.status === 'success') {
          commit('SET_ERP_RUNNING_STATUS', !!(data.data?.running));
        }
      } catch (e) {
        console.error('[trialBalance/stopErpPipeline]: Failed to stop ERP TB pipeline', e);
      }
    },

    async syncERP({ commit, state, rootState, dispatch }, { id, startDate, endDate }) {
      commit('SET_LOADING', true);
      try {
        if (!rootState.erpEnabled?.tbErpEnabled) return;
        if (state.erpRunningStatus) {
          await dispatch('stopErpPipeline', { id, startDate, endDate });
        }
        const dt = await $axios.post('v2/erp/sync-trialbalance', { project: id, startDate, endDate });
        const msg = dt.data.message;
        if (dt.data.status === 'success') {
          commit('sbSuccess', msg || 'Sync successful!', { root: true });
        } else {
          commit('sbError', msg || 'Sync failed!', { root: true });
        }
      } catch (err) {
        console.error('[trialBalance/syncERP]:', err);
        commit('sbError', err?.message || err || 'Unknown error!', { root: true });
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchLocationTasklist({ commit }, { id, locationid }) {
      try {
        const dt = await $axios.post(`v2/financialclose/analysis/getlocationbasedtasklist/${id}/c/${locationid}`);
        if (dt.data.status === 'success') {
          const taskList = dt.data.data.map(item => {
            item.displayText = item.ref + ' - ' + item.description;
            return item;
          });
          commit('SET_LOCATION_TASK_LIST', taskList);
        } else {
          throw new Error(dt.data.message);
        }
      } catch (err) {
        console.error('[trialBalance/fetchLocationTasklist]:', err);
        commit('sbError', err?.message || err || 'Unknown error!', { root: true });
      }
    },

    async editTrialBalance({ commit }, { projectId, trialId, data }) {
      try {
        const dt = await $axios.post(`/v2/financialclose/analysis/edittrialbalance/${projectId}/c/${trialId}`, { data });
        if (dt.data.status === 'success') {
          commit('sbSuccess', 'Update successful', { root: true });
          return true;
        } else {
          throw new Error(dt.data.message);
        }
      } catch (err) {
        console.error('[trialBalance/editTrialBalance]:', err);
        commit('sbError', err?.message || 'Unknown error!', { root: true });
        return false;
      }
    },
  },
};
